import { owedCounts, type OwedPayload } from './owed-view'

// Shared by the nav's reads and the Owed screen's fresh payloads. A save
// invalidates reads already in flight before the coalesced refresh starts.
let sequence = 0

export function nextOwedBadgeSequence(): number {
  return ++sequence
}

// The count going up (tidy-up item 2; Liam, 20 Oct 2026: "Added to Owed ... and the Owed count at the bottom goes up by
// one"; docs/plans/tidy-up/AGREED.md section 2). The number is always the server's, never a guessed +1: Not paid yet
// does not always change it (a visit already counted, one paid from credit, a customer taken off the round). When a
// read shows it higher, the badge pops once, in Not paid yet's orange: after any pop-up open over it has closed (on a
// phone the sheet covers the tab bar), and only after reading the real count again, so an Undo meanwhile cancels it.
// Nothing moves on a phone set to reduce motion; the number still changes.
let shown: number | null = null
let riseFrom: number | null = null
let waiting = false
let reread: (() => Promise<void>) | null = null

// main.ts's refresh: the count read again from the server.
export function onOwedBadgeReread(fn: () => Promise<void>): void {
  reread = fn
}

export function setOwedBadge(payload: OwedPayload, today: string, readSequence?: number): void {
  if (readSequence !== undefined && readSequence !== sequence) return
  nextOwedBadgeSequence()
  const badge = document.querySelector<HTMLElement>('#owed-badge')
  if (!badge) return
  // Visits on a sent invoice are asked for through the invoice, never counted on their own: the same rule as the Owed page
  // (screens/owed.ts withoutInvoiced), so the tab and the page say the same number (GPT-6.1 and Sonnet, 26 Oct 2026).
  const keep = <T extends { on_invoice?: string | null }>(list: T[]): T[] => list.filter((c) => !c.on_invoice)
  const counted = { ...payload, cleans: keep(payload.cleans as (typeof payload.cleans[number] & { on_invoice?: string | null })[]),
    ...(payload.unrecorded ? { unrecorded: { ...payload.unrecorded, cleans: keep((payload.unrecorded.cleans ?? []) as ({ on_invoice?: string | null } & (typeof payload.cleans)[number])[]) } } : {}) }
  const n = owedCounts(counted as OwedPayload, today).all
  badge.textContent = n > 0 ? ` ${n}` : ''
  badge.className = n > 0 ? 'owed-badge' : ''
  if (shown !== null && n > shown && riseFrom === null) {
    riseFrom = shown
    void popWhenSeen()
  } else if (riseFrom !== null && n <= riseFrom) riseFrom = null
  shown = n
}

async function popWhenSeen(): Promise<void> {
  if (waiting) return
  waiting = true
  await new Promise<void>((resolve) => {
    const open = document.querySelector<HTMLElement>('dialog[open]')
    if (open && typeof open.addEventListener === 'function') open.addEventListener('close', () => resolve(), { once: true })
    else resolve()
  })
  try { await reread?.() } catch { /* no signal: the number shown stands */ }
  waiting = false
  const from = riseFrom
  riseFrom = null
  const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
  if (from === null || shown === null || shown <= from || still) return
  const badge = document.querySelector<HTMLElement>('#owed-badge')
  if (typeof badge?.animate !== 'function') return
  const lit = { backgroundColor: '#e98a2e', color: '#fff', boxShadow: '0 0 0 4px #fdebd8' }
  badge.animate([{ transform: 'scale(1)', ...lit }, { transform: 'scale(1.25)', offset: 0.35, ...lit }, { transform: 'scale(1)', offset: 0.75, ...lit }, { transform: 'scale(1)' }],
    { duration: 700, easing: 'cubic-bezier(.2,.9,.3,1.4)' })
}
